// ./src/components/features/Submissions/SubmissionsFilters/SubmissionsFilters.tsx
import React, { useMemo } from "react";
import { Group, Select, ComboboxItem, Text } from "@mantine/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { SubmissionFilter } from "@/utils/api";
import { SUBMISSION_STATUSES, STATUS_CONFIG, ICON_SIZE_MAP } from "@/config";
import { AppIcon, DateRangeFilter, DateRange } from "@/components/generic";

// Enable UTC plugin for dayjs
dayjs.extend(utc);

// ============================================
// Types
// ============================================

export interface SubmissionsFiltersProps {
  /** Current filter values */
  filters: Partial<SubmissionFilter>;
  /** Callback when filters change */
  onFiltersChange: (filters: Partial<SubmissionFilter>) => void;
  /** Whether filters are disabled */
  disabled?: boolean;
  /** Size of the filter controls */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

// ============================================
// Filter Options
// ============================================

// Status options for the dropdown (derived from config)
const STATUS_OPTIONS: ComboboxItem[] = [
  { value: "", label: "All Statuses" },
  {
    value: String(SUBMISSION_STATUSES.RECEIVED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.RECEIVED].label,
  },
  {
    value: String(SUBMISSION_STATUSES.REVIEWED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.REVIEWED].label,
  },
  {
    value: String(SUBMISSION_STATUSES.ACCEPTED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.ACCEPTED].label,
  },
  {
    value: String(SUBMISSION_STATUSES.DECLINED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.DECLINED].label,
  },
  {
    value: String(SUBMISSION_STATUSES.SPAM),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.SPAM].label,
  },
  {
    value: String(SUBMISSION_STATUSES.BLOCKED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.BLOCKED].label,
  },
];

// Files filter options
const FILES_OPTIONS: ComboboxItem[] = [
  { value: "", label: "All Submissions" },
  { value: "with", label: "With Files" },
  { value: "without", label: "Without Files" },
];

// ============================================
// Helper Functions
// ============================================

/**
 * Parse API date string (UTC) to local Date object for display
 * API sends UTC datetime, we convert to local for the picker
 */
const parseApiDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;
  const parsed = dayjs.utc(dateStr).local();
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * Convert local Date to UTC datetime string for API
 * @param date - Local date from the picker
 * @param isEndOfDay - If true, set time to 23:59:59, otherwise 00:00:00
 */
const formatApiDate = (
  date: Date | null,
  isEndOfDay: boolean
): string | null => {
  if (!date) return null;

  let localDate = dayjs(date);

  if (isEndOfDay) {
    // Set to end of day in local time: 23:59:59
    localDate = localDate.hour(23).minute(59).second(59).millisecond(999);
  } else {
    // Set to start of day in local time: 00:00:00
    localDate = localDate.hour(0).minute(0).second(0).millisecond(0);
  }

  // Convert to UTC and format as ISO string
  return localDate.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
};

// ============================================
// Component
// ============================================

export const SubmissionsFilters: React.FC<SubmissionsFiltersProps> = ({
  filters,
  onFiltersChange,
  disabled = false,
  size = "sm",
}) => {
  const iconSize = ICON_SIZE_MAP[size] ?? 14;

  // Get current status value for the select
  const statusValue = filters.status?.length ? String(filters.status[0]) : "";

  // Get current files filter value
  const filesValue =
    filters.hasFiles === true
      ? "with"
      : filters.hasFiles === false
      ? "without"
      : "";

  // Get current date range value - convert from UTC API dates to local for display
  const dateRangeValue: DateRange = useMemo(
    () => ({
      from: parseApiDate(filters.fromDate),
      to: parseApiDate(filters.toDate),
    }),
    [filters.fromDate, filters.toDate]
  );

  // Get status config by value for rendering
  const getStatusConfig = (value: string) => {
    if (!value) return null;
    const statusCode = parseInt(value, 10);
    return STATUS_CONFIG[statusCode] ?? null;
  };

  // Render status option with icon
  const renderStatusOption = ({ option }: { option: ComboboxItem }) => {
    const config = getStatusConfig(option.value);
    if (!config) {
      return <Text size={size}>{option.label}</Text>;
    }
    return (
      <Group gap="sm" wrap="nowrap">
        <AppIcon
          iconName={config.icon}
          size={iconSize}
          style={{ color: `var(--mantine-color-${config.color}-6)` }}
        />
        <Text size={size}>{option.label}</Text>
      </Group>
    );
  };

  // Handle status filter change
  const handleStatusChange = (value: string | null) => {
    const newFilters = { ...filters };

    if (value && value !== "") {
      newFilters.status = [parseInt(value, 10)];
    } else {
      delete newFilters.status;
    }

    onFiltersChange(newFilters);
  };

  // Handle files filter change
  const handleFilesChange = (value: string | null) => {
    const newFilters = { ...filters };

    if (value === "with") {
      newFilters.hasFiles = true;
    } else if (value === "without") {
      newFilters.hasFiles = false;
    } else {
      delete newFilters.hasFiles;
    }

    onFiltersChange(newFilters);
  };

  // Handle date range filter change
  const handleDateRangeChange = (range: DateRange) => {
    const newFilters = { ...filters };

    // Convert local dates to UTC for API
    // fromDate: start of day (00:00:00) in local time -> UTC
    // toDate: end of day (23:59:59) in local time -> UTC
    const fromDate = formatApiDate(range.from, false);
    const toDate = formatApiDate(range.to, true);

    if (fromDate) {
      newFilters.fromDate = fromDate;
    } else {
      delete newFilters.fromDate;
    }

    if (toDate) {
      newFilters.toDate = toDate;
    } else {
      delete newFilters.toDate;
    }

    onFiltersChange(newFilters);
  };

  // Get left section icon for status select
  const currentStatusConfig = getStatusConfig(statusValue);
  const statusLeftSection = currentStatusConfig ? (
    <AppIcon
      iconName={currentStatusConfig.icon}
      size={iconSize}
      style={{ color: `var(--mantine-color-${currentStatusConfig.color}-6)` }}
    />
  ) : (
    <AppIcon iconName="Filter" size={iconSize} />
  );

  // Get left section icon for files select
  const filesLeftSection =
    filesValue === "with" ? (
      <AppIcon iconName="Paperclip" size={iconSize} />
    ) : filesValue === "without" ? (
      <AppIcon iconName="FileOff" size={iconSize} />
    ) : (
      <AppIcon iconName="Files" size={iconSize} />
    );

  return (
    <Group gap="sm">
      <Select
        placeholder="All Statuses"
        data={STATUS_OPTIONS}
        value={statusValue}
        onChange={handleStatusChange}
        disabled={disabled}
        clearable
        size={size}
        w={160}
        leftSection={statusLeftSection}
        renderOption={renderStatusOption}
        comboboxProps={{ withinPortal: true }}
      />
      <Select
        placeholder="All Submissions"
        data={FILES_OPTIONS}
        value={filesValue}
        onChange={handleFilesChange}
        disabled={disabled}
        clearable
        size={size}
        w={170}
        leftSection={filesLeftSection}
        comboboxProps={{ withinPortal: true }}
      />
      <DateRangeFilter
        value={dateRangeValue}
        onChange={handleDateRangeChange}
        disabled={disabled}
        size={size}
        placeholder="All Time"
      />
    </Group>
  );
};
